import { Link, useLocation, useNavigate } from 'react-router-dom';
import useAuthStore from '@/store/authStore';
import authService from '@/services/auth.service';
import { clearAuth } from '@/utils/auth';
import { useState, useRef, useEffect } from 'react';
import '@/styles/index.css';
import shellLogo from '@/assets/images/logo1.png';

/**
 * Header Component - Heritage Warm Gold Design with Centered Branding
 */
const Header = () => {
  const { user, clearAuth: clearStoreAuth, logout: storeLogout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [vastuKnowledgeOpen, setVastuKnowledgeOpen] = useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const dropdownRef = useRef(null);
  const servicesDropdownRef = useRef(null);
  const userMenuRef = useRef(null);

  const handleLogout = async () => {
    setUserMenuOpen(false);
    try {
      await authService.logout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      clearAuth();
      if (clearStoreAuth) clearStoreAuth();
      if (storeLogout) storeLogout();
      navigate('/login');
    }
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setVastuKnowledgeOpen(false);
    setServicesDropdownOpen(false);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen((prev) => !prev);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setVastuKnowledgeOpen(false);
      }
      if (servicesDropdownRef.current && !servicesDropdownRef.current.contains(event.target)) {
        setServicesDropdownOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdowns on route change
  useEffect(() => {
    setVastuKnowledgeOpen(false);
    setServicesDropdownOpen(false);
    setMobileMenuOpen(false);
    setUserMenuOpen(false);
  }, [location.pathname]);

  // Vastu Knowledge dropdown items matching screenshot
  const vastuKnowledgeItems = [
    {
      to: '/vastu-knowledge/transformative-power',
      label: (
        <span>
          The Transformative Power<br />of Vastushastra
        </span>
      ),
    },
    { to: '/vastu-knowledge/vastumandala', label: 'Vastumandala' },
    { to: '/ayadi-calculations', label: 'Ayadi Calculations' },
    { to: '/vastu-plants-and-trees', label: 'Plants and Vastushastra' },
    { to: '/vastu-knowledge/advance-topics', label: 'Advanced Vastu Topics' },
    { to: '/vastu-dosh-symptoms', label: 'Symptoms of Harmful Vastu Energies' },
    { to: '/blog', label: 'Articles' },
    { to: '/vastu-courses?tab=books', label: 'Books' },
    { to: '/vastu-courses?tab=workshops', label: 'Workshops' },
    { to: '/vastu-courses?tab=courses', label: 'Courses' },
  ];

  // Services dropdown items
  const servicesItems = [
    { to: '/our-services/architectural-review', label: 'Online Review of Architectural Plans' },
    { to: '/online-vastu-self-assessment', label: 'Online Vastu Self-Assessment' },
    { to: '/our-services/online-consultation', label: 'Schedule an Online Consultation' },
    { to: '/our-services/onsite-consultation', label: 'Onsite Consultation' },
  ];

  return (
    <header className="bg-[#F5CE9F] border-b-2 border-[#BD6A22] sticky top-0 z-50 shadow-sm">
      <nav className="max-w-[1340px] mx-auto px-6 sm:px-8 lg:px-10 py-1.5 sm:py-2">
        {/* Desktop Balanced Luxury Layout */}
        <div className="hidden lg:flex items-center justify-between w-full">
          
          {/* Left Navigation: Home, Vastu Knowledge ▾, Services ▾ */}
          <div className="flex items-center gap-7 xl:gap-9 font-sans text-[14px] text-[#2D1B0D] min-w-[290px]">
            <Link
              to="/"
              className={`hover:text-[#9E4912] transition-colors py-1 ${
                location.pathname === '/' ? 'font-semibold text-[#9E4912]' : 'font-normal'
              }`}
            >
              Home
            </Link>

            {/* Vastu Knowledge with dropdown */}
            <div 
              className="relative inline-flex items-center" 
              ref={dropdownRef}
              onMouseEnter={() => setVastuKnowledgeOpen(true)}
              onMouseLeave={() => setVastuKnowledgeOpen(false)}
            >
              <button
                type="button"
                onClick={() => setVastuKnowledgeOpen((prev) => !prev)}
                className="relative py-1 flex items-center hover:text-[#9E4912] transition-colors focus:outline-none cursor-pointer"
              >
                <span
                  className={
                    location.pathname.startsWith('/vastu') ||
                    location.pathname === '/ayadi-calculations' ||
                    location.pathname === '/blog'
                      ? 'font-semibold text-[#9E4912]'
                      : 'font-normal'
                  }
                >
                  Vastu Knowledge
                </span>
                {/* Downward indicator chevron */}
                <svg
                  className={`w-3.5 h-2 text-[#9E4912] ml-1.5 transition-transform duration-200 ${
                    vastuKnowledgeOpen ? 'rotate-180' : ''
                  }`}
                  viewBox="0 0 16 10"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <path d="M2 2L8 8L14 2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>

              {/* Dropdown Menu - Aligned directly below the navbar */}
              {vastuKnowledgeOpen && (
                <div 
                  className="absolute top-full left-0 pt-2 z-50 animate-fadeIn"
                >
                  <div className="w-[240px] bg-[#F7DFBF] border border-[#BD6A22] rounded-b-md shadow-xl py-2.5 px-4">
                    <div className="space-y-2 text-[13px] text-[#26180E] font-sans">
                      {vastuKnowledgeItems.map((item, index) => (
                        <Link
                          key={index}
                          to={item.to}
                          onClick={() => setVastuKnowledgeOpen(false)}
                          className="block hover:text-[#9E4912] transition-colors leading-snug py-0.5"
                        >
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Services with matching dropdown chevron */}
            <div 
              className="relative inline-flex items-center" 
              ref={servicesDropdownRef}
              onMouseEnter={() => setServicesDropdownOpen(true)}
              onMouseLeave={() => setServicesDropdownOpen(false)}
            >
              <button
                type="button"
                onClick={() => setServicesDropdownOpen((prev) => !prev)}
                className="relative py-1 flex items-center hover:text-[#9E4912] transition-colors focus:outline-none cursor-pointer"
              >
                <span
                  className={
                    location.pathname.startsWith('/our-services') ||
                    location.pathname.startsWith('/online-vastu')
                      ? 'font-semibold text-[#9E4912]'
                      : 'font-normal'
                  }
                >
                  Services
                </span>
                {/* Downward indicator chevron identical to Vastu Knowledge */}
                <svg
                  className={`w-3.5 h-2 text-[#9E4912] ml-1.5 transition-transform duration-200 ${
                    servicesDropdownOpen ? 'rotate-180' : ''
                  }`}
                  viewBox="0 0 16 10"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <path d="M2 2L8 8L14 2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>

              {servicesDropdownOpen && (
                <div 
                  className="absolute top-full left-0 pt-2 z-50 animate-fadeIn"
                >
                  <div className="w-[260px] bg-[#F7DFBF] border border-[#BD6A22] rounded-b-md shadow-xl py-2.5 px-4">
                    <div className="space-y-2 text-[13px] text-[#26180E] font-sans">
                      {servicesItems.map((item, index) => (
                        <Link
                          key={index}
                          to={item.to}
                          onClick={() => setServicesDropdownOpen(false)}
                          className="block hover:text-[#9E4912] transition-colors leading-snug py-0.5"
                        >
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Center Brand Logo & Typography - Perfectly Centered */}
          <Link to="/" className="flex flex-col items-center justify-center text-center px-4 group flex-shrink-0">
            <img src={shellLogo} alt="Vāstusindhu Conch" className="w-6 h-auto mb-0.5" />
            <span className="text-xl font-serif tracking-[0.22em] text-[#2D1B0D] [font-variant:small-caps] font-medium leading-none">
              Vāstusindhu
            </span>
            <p className="font-serif italic text-[11px] text-[#4A3525] mt-1 tracking-normal leading-tight whitespace-nowrap">
              Vastu Shastra consultant with over three decades of practice
            </p>
          </Link>

          {/* Right Navigation: FAQ, Let's Connect, Log in / Register */}
          <div className="flex items-center justify-end gap-6 xl:gap-8 font-sans text-[14px] text-[#2D1B0D] min-w-[290px]">
            <Link
              to="/faqs"
              className={`hover:text-[#9E4912] transition-colors py-1 ${
                location.pathname === '/faqs' ? 'font-semibold text-[#9E4912]' : 'font-normal'
              }`}
            >
              FAQ
            </Link>

            <Link
              to="/contact-us"
              className={`hover:text-[#9E4912] transition-colors py-1 whitespace-nowrap ${
                location.pathname === '/contact-us' ? 'font-semibold text-[#9E4912]' : 'font-normal'
              }`}
            >
              Let&apos;s Connect
            </Link>

            {/* Auth Button */}
            {user ? (
              <div className="relative inline-flex items-center" ref={userMenuRef}>
                <button
                  type="button"
                  onClick={() => setUserMenuOpen((prev) => !prev)}
                  className="inline-flex items-center justify-center bg-[#9E4912] hover:bg-[#833B0A] text-white font-sans text-xs sm:text-[13px] font-semibold px-5 sm:px-6 py-2 sm:py-2.5 rounded-full transition-all duration-200 shadow-sm whitespace-nowrap gap-1.5 cursor-pointer leading-none"
                >
                  <span>{user.name ? user.name.split(' ')[0] : 'My Account'}</span>
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {userMenuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-44 bg-[#F7DFBF] border border-[#BD6A22] rounded-lg shadow-xl py-2 z-50">
                    <Link
                      to={user?.role?.toUpperCase() === 'ADMIN' ? '/admin/dashboard' : '/dashboard'}
                      onClick={() => setUserMenuOpen(false)}
                      className="block px-4 py-1.5 text-xs text-[#26180E] hover:bg-[#F2D7B2] hover:text-[#9E4912]"
                    >
                      Dashboard
                    </Link>
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-1.5 text-xs text-red-700 hover:bg-[#F2D7B2] cursor-pointer"
                    >
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/login"
                className="inline-flex items-center justify-center bg-[#9E4912] hover:bg-[#833B0A] text-white font-sans text-xs sm:text-[13px] font-semibold px-6 sm:px-7 py-2 sm:py-2.5 rounded-full transition-all duration-200 shadow-sm hover:shadow whitespace-nowrap leading-none"
              >
                Log in / Register
              </Link>
            )}
          </div>

        </div>

        {/* Mobile Header Bar */}
        <div className="lg:hidden flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={shellLogo} alt="Vāstusindhu Conch" className="w-6 h-auto" />
            <div className="flex flex-col">
              <span className="text-base font-serif tracking-[0.18em] text-[#2D1B0D] [font-variant:small-caps] font-medium leading-none">
                Vāstusindhu
              </span>
              <span className="font-serif italic text-[9px] text-[#4A3525]">
                Vastu Shastra Consultant
              </span>
            </div>
          </Link>

          <button
            onClick={toggleMobileMenu}
            className="p-1.5 text-[#2D1B0D] hover:text-[#9E4912] focus:outline-none"
            aria-label="Toggle navigation menu"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Navigation Dropdown */}
        {mobileMenuOpen && (
          <div className="lg:hidden mt-3 pt-3 border-t border-[#D5A773] space-y-2.5 font-sans text-sm text-[#2D1B0D] animate-fadeIn pb-2">
            <Link to="/" onClick={closeMobileMenu} className="block py-1 hover:text-[#9E4912]">
              Home
            </Link>

            {/* Mobile Vastu Knowledge Accordion */}
            <div>
              <button
                type="button"
                onClick={() => setVastuKnowledgeOpen((prev) => !prev)}
                className="w-full flex items-center justify-between py-1 text-left font-medium"
              >
                <span>Vastu Knowledge</span>
                <svg
                  className={`w-4 h-4 text-[#9E4912] transition-transform ${
                    vastuKnowledgeOpen ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {vastuKnowledgeOpen && (
                <div className="pl-4 pt-2 space-y-2 border-l border-[#C88A4C] ml-1 mt-1">
                  {vastuKnowledgeItems.map((item, index) => (
                    <Link
                      key={index}
                      to={item.to}
                      onClick={closeMobileMenu}
                      className="block text-xs sm:text-sm text-[#382415] hover:text-[#9E4912]"
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Mobile Services */}
            <div>
              <button
                type="button"
                onClick={() => setServicesDropdownOpen((prev) => !prev)}
                className="w-full flex items-center justify-between py-1 text-left font-medium"
              >
                <span>Services</span>
                <svg
                  className={`w-4 h-4 text-[#9E4912] transition-transform ${
                    servicesDropdownOpen ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {servicesDropdownOpen && (
                <div className="pl-4 pt-2 space-y-2 border-l border-[#C88A4C] ml-1 mt-1">
                  {servicesItems.map((item, index) => (
                    <Link
                      key={index}
                      to={item.to}
                      onClick={closeMobileMenu}
                      className="block text-xs sm:text-sm text-[#382415] hover:text-[#9E4912]"
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link to="/faqs" onClick={closeMobileMenu} className="block py-1 hover:text-[#9E4912]">
              FAQ
            </Link>

            <Link to="/contact-us" onClick={closeMobileMenu} className="block py-1 hover:text-[#9E4912]">
              Let&apos;s Connect
            </Link>

            {/* Mobile Auth Button */}
            <div className="pt-2">
              {user ? (
                <div className="flex flex-col gap-2">
                  <Link
                    to={user?.role?.toUpperCase() === 'ADMIN' ? '/admin/dashboard' : '/dashboard'}
                    onClick={closeMobileMenu}
                    className="block text-center bg-[#9E4912] hover:bg-[#833B0A] text-white py-2.5 px-6 rounded-full font-semibold text-xs"
                  >
                    Dashboard
                  </Link>
                  <button
                    type="button"
                    onClick={() => {
                      closeMobileMenu();
                      handleLogout();
                    }}
                    className="block w-full text-center bg-transparent border border-red-700 text-red-700 hover:bg-red-50 py-2 px-6 rounded-full font-semibold text-xs cursor-pointer"
                  >
                    Sign Out
                  </button>
                </div>
              ) : (
                <Link
                  to="/login"
                  onClick={closeMobileMenu}
                  className="block text-center bg-[#9E4912] hover:bg-[#833B0A] text-white py-2.5 px-6 rounded-full font-semibold text-xs"
                >
                  Log in / Register
                </Link>
              )}
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
